import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "pos-manager-wrapper"
}
const _hoisted_2 = { class: "pos-manager-content" }
const _hoisted_3 = { class: "status-bar" }
const _hoisted_4 = { class: "status-item" }
const _hoisted_5 = { class: "status-item" }
const _hoisted_6 = { class: "action-bar" }
const _hoisted_7 = { class: "transaction-table" }
const _hoisted_8 = { class: "txn-id" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "statistics" }

import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import {
  ElButton,
  ElDrawer,
  ElTag,
  ElTable,
  ElTableColumn,
  ElMessage,
  ElMessageBox,
} from "element-plus";
import n5PosManager from "@/utils/pos/N5PosManager";
import { Http_getPayMentList } from "@/http/apiFunction";
import {
  PosConnectionStatus,
  PosHealthStatus,
  type TransactionRecord,
  type TransactionRecordEvent,
} from "@/utils/pos/N5PosManager";
import dayjs from "dayjs";

// 状态数据

export default /*@__PURE__*/_defineComponent({
  __name: 'PosManager',
  setup(__props) {

const drawerVisible = ref(false);
const loading = ref(false);
const connecting = ref(false);
const disconnecting = ref(false);
// 是否显示POS机管理按钮
const showPosManagerButton = ref(false);
// 结算 loading
const settleLoading = ref(false);

// POS机状态
const serialConnectionStatus = ref<PosConnectionStatus>(
  n5PosManager.connectionStatus
);
const posHealthStatus = ref<PosHealthStatus>(n5PosManager.healthStatus);
const transactionRecords = ref<TransactionRecord[]>(
  n5PosManager.getTransactionRecords()
);

// 交易记录（按时间倒序排列，最新的在最上面）
const sortedTransactionRecords = computed(() => {
  return [...transactionRecords.value].sort(
    (a, b) => b.timestamp - a.timestamp
  );
});

// 连接状态显示
const connectionStatusText = computed(() => {
  switch (serialConnectionStatus.value) {
    case PosConnectionStatus.CONNECTED:
      return "已连接";
    case PosConnectionStatus.CONNECTING:
      return "连接中";
    case PosConnectionStatus.DISCONNECTED:
      return "未连接";
    case PosConnectionStatus.ERROR:
      return "连接错误";
    default:
      return "未知";
  }
});

const connectionStatusType = computed(() => {
  switch (serialConnectionStatus.value) {
    case PosConnectionStatus.CONNECTED:
      return "success";
    case PosConnectionStatus.CONNECTING:
      return "warning";
    case PosConnectionStatus.DISCONNECTED:
      return "info";
    case PosConnectionStatus.ERROR:
      return "danger";
    default:
      return "info";
  }
});

// 健康状态显示
const healthStatusText = computed(() => {
  switch (posHealthStatus.value) {
    case PosHealthStatus.SUCCESS:
      return "正常";
    case PosHealthStatus.FAIL:
      return "主机拒绝";
    case PosHealthStatus.ECR_BUSY:
      return "POS机繁忙";
    default:
      return "未知";
  }
});

const healthStatusType = computed(() => {
  switch (posHealthStatus.value) {
    case PosHealthStatus.SUCCESS:
      return "success";
    case PosHealthStatus.FAIL:
      return "danger";
    case PosHealthStatus.ECR_BUSY:
      return "warning";
    default:
      return "info";
  }
});

// 统计信息
const successCount = computed(() => {
  return transactionRecords.value.filter(
    (r) => r.requestStatus === "completed" && r.status === "00"
  ).length;
});

const failedCount = computed(() => {
  return transactionRecords.value.filter(
    (r) =>
      r.requestStatus === "failed" ||
      r.requestStatus === "timeout" ||
      r.requestStatus === "aborted" ||
      (r.requestStatus === "completed" && r.status !== "00")
  ).length;
});

const pendingCount = computed(() => {
  return transactionRecords.value.filter(
    (r) => r.requestStatus === "pending" || r.requestStatus === "sent"
  ).length;
});

// 监听器引用
let unsubscribeSerialStatus: (() => void) | null = null;
let unsubscribeTransactionRecord: (() => void) | null = null;

/**
 * 检查是否有POS机支付方式
 * 判断支付方式列表中是否包含POS机相关的支付类型
 */
const checkPosPaymentAvailable = async () => {
  try {
    const res = await Http_getPayMentList();
    if (res.code === 0 && res.data) {
      // POS机相关的支付类型：1020、1021、1022、1024
      const posPayTypes = [1020, 1021, 1022, 1024];

      // 检查是否存在任意一个POS支付类型
      const hasPosPayment = res.data.some((item) =>
        posPayTypes.includes(item.payType)
      );

      showPosManagerButton.value = hasPosPayment;
    }
  } catch (error) {
    console.error("获取支付方式列表失败:", error);
    // 发生错误时默认不显示按钮
    showPosManagerButton.value = false;
  }
};

// 生命周期钩子
onMounted(() => {
  // 检查是否有POS机支付方式
  checkPosPaymentAvailable();

  // 添加串口状态监听器
  unsubscribeSerialStatus = n5PosManager.addSerialStatusListener(
    ({ connectionStatus, healthStatus }) => {
      serialConnectionStatus.value = connectionStatus;
      posHealthStatus.value = healthStatus;
    }
  );

  // 添加交易记录监听器
  unsubscribeTransactionRecord = n5PosManager.addTransactionRecordListener(
    (event: TransactionRecordEvent) => {
      transactionRecords.value = [...event.records];
    }
  );

  // 初始化数据
  serialConnectionStatus.value = n5PosManager.connectionStatus;
  posHealthStatus.value = n5PosManager.healthStatus;
  transactionRecords.value = n5PosManager.getTransactionRecords();
});

onBeforeUnmount(() => {
  // 移除监听器
  if (unsubscribeSerialStatus) {
    unsubscribeSerialStatus();
  }
  if (unsubscribeTransactionRecord) {
    unsubscribeTransactionRecord();
  }
});

// 方法
const handleConnect = async () => {
  try {
    connecting.value = true;
    await n5PosManager.connect();
    ElMessage.success("POS机连接成功");
  } catch (error) {
    ElMessage.error(
      `连接失败: ${error instanceof Error ? error.message : "未知错误"}`
    );
  } finally {
    connecting.value = false;
  }
};

const handleDisconnect = async () => {
  try {
    disconnecting.value = true;
    await n5PosManager.disconnect();
    ElMessage.success("POS机已断开连接");
  } catch (error) {
    ElMessage.error(
      `断开连接失败: ${error instanceof Error ? error.message : "未知错误"}`
    );
  } finally {
    disconnecting.value = false;
  }
};

const handleClearConnection = async () => {
  try {
    await ElMessageBox.confirm(
      "清理连接将断开当前串口并清除自动连接记录，下次连接时需要重新选择串口设备。确定要继续吗？",
      "提示",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    await n5PosManager.clearSerialConnection();
    ElMessage.success("串口连接已清理，本地存储已清除");
  } catch (error) {
    if (error !== "cancel") {
      ElMessage.error(
        `清理连接失败: ${error instanceof Error ? error.message : "未知错误"}`
      );
    }
  }
};

const handleSettle = async () => {
  settleLoading.value = true;
  const resutle = await n5PosManager.settleMent();
  settleLoading.value = false;
  ElMessage({
    type: resutle.code === 0 ? "success" : "error",
    message:
      resutle.code === 0 ? "清机结算成功" : `清机结算失败,${resutle.msg}`,
  });
};

// 格式化金额
const formatAmount = (amount: number): string => {
  return `¥${amount.toFixed(2)}`;
};

// 格式化时间
const formatTime = (timestamp: number): string => {
  return dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
};

// 获取请求状态类型
const getRequestStatusType = (
  status: string
): "success" | "warning" | "danger" | "info" => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
    case "sent":
      return "warning";
    case "failed":
    case "timeout":
    case "aborted":
      return "danger";
    default:
      return "info";
  }
};

// 获取请求状态文本
const getRequestStatusText = (status: string): string => {
  switch (status) {
    case "pending":
      return "等待中";
    case "sent":
      return "已发送";
    case "completed":
      return "已完成";
    case "failed":
      return "失败";
    case "timeout":
      return "超时";
    case "aborted":
      return "已取消";
    default:
      return status;
  }
};

// 获取信息文本的样式类名
const getMessageClass = (status: string): string => {
  switch (status) {
    case "completed":
      return "success-message";
    case "pending":
    case "sent":
      return "warning-message";
    case "failed":
    case "timeout":
    case "aborted":
      return "error-message";
    default:
      return "info-message";
  }
};

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return (showPosManagerButton.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(ElButton), {
          class: "pos_manager_btn",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (drawerVisible.value = true))
        }, {
          default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
            _createTextVNode(" POS机管理 ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_unref(ElDrawer), {
          modelValue: drawerVisible.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((drawerVisible).value = $event)),
          title: "POS机管理",
          size: 920,
          "close-on-click-modal": false,
          "destroy-on-close": ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _cache[3] || (_cache[3] = _createElementVNode("span", { class: "status-label" }, "连接状态：", -1)),
                  _createVNode(_unref(ElTag), {
                    type: connectionStatusType.value,
                    size: "large"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(connectionStatusText.value), 1)
                    ]),
                    _: 1
                  }, 8, ["type"])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "status-label" }, "健康状态：", -1)),
                  _createVNode(_unref(ElTag), {
                    type: healthStatusType.value,
                    size: "large"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(healthStatusText.value), 1)
                    ]),
                    _: 1
                  }, 8, ["type"])
                ])
              ]),
              _createElementVNode("div", _hoisted_6, [
                (
              serialConnectionStatus.value === _unref(PosConnectionStatus).DISCONNECTED ||
              serialConnectionStatus.value === _unref(PosConnectionStatus).ERROR
            )
                  ? (_openBlock(), _createBlock(_unref(ElButton), {
                      key: 0,
                      type: "primary",
                      onClick: handleConnect,
                      loading: connecting.value
                    }, {
                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                        _createTextVNode(" 连接POS机 ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["loading"]))
                  : (_openBlock(), _createBlock(_unref(ElButton), {
                      key: 1,
                      type: "danger",
                      onClick: handleDisconnect,
                      loading: disconnecting.value
                    }, {
                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createTextVNode(" 断开连接 ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["loading"])),
                _createVNode(_unref(ElButton), {
                  type: "warning",
                  onClick: handleClearConnection,
                  disabled: 
              serialConnectionStatus.value === _unref(PosConnectionStatus).CONNECTING
            
                }, {
                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode(" 清理连接 ", -1)
                  ]))]),
                  _: 1
                }, 8, ["disabled"]),
                _createVNode(_unref(ElButton), {
                  type: "primary",
                  onClick: handleSettle,
                  style: {"margin-left":"auto"},
                  loading: settleLoading.value
                }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode(" 清机结算 ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _withDirectives((_openBlock(), _createBlock(_unref(ElTable), {
                  data: sortedTransactionRecords.value,
                  border: "",
                  stripe: "",
                  "max-height": "500",
                  style: {"width":"100%"}
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ElTableColumn), {
                      type: "index",
                      label: "序号",
                      width: "60",
                      align: "center"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(scope.$index + 1), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElTableColumn), {
                      prop: "txnId",
                      label: "交易ID",
                      width: "150"
                    }, {
                      default: _withCtx((scope) => [
                        _createElementVNode("span", _hoisted_8, _toDisplayString(scope.row.txnId || "-"), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElTableColumn), {
                      prop: "eventName",
                      label: "交易类型",
                      width: "80",
                      align: "center"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_unref(ElTag), { size: "small" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(scope.row.eventName), 1)
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElTableColumn), {
                      prop: "amount",
                      label: "金额",
                      width: "100",
                      align: "right"
                    }, {
                      default: _withCtx((scope) => [
                        (scope.row.amount)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(scope.row.amount), 1))
                          : (_openBlock(), _createElementBlock("span", _hoisted_10, "-"))
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElTableColumn), {
                      prop: "timestamp",
                      label: "交易时间",
                      width: "140",
                      align: "right"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(formatTime(scope.row.timestamp)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElTableColumn), {
                      prop: "requestStatus",
                      label: "请求状态",
                      width: "80",
                      align: "center"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_unref(ElTag), {
                          type: getRequestStatusType(scope.row.requestStatus)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(getRequestStatusText(scope.row.requestStatus)), 1)
                          ]),
                          _: 2
                        }, 1032, ["type"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElTableColumn), {
                      prop: "status",
                      label: "状态码",
                      width: "60",
                      align: "center"
                    }, {
                      default: _withCtx((scope) => [
                        (scope.row.status)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                              _createVNode(_unref(ElTag), {
                                type: scope.row.status === '00' ? 'success' : 'danger',
                                size: "small"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(scope.row.status), 1)
                                ]),
                                _: 2
                              }, 1032, ["type"])
                            ]))
                          : (_openBlock(), _createElementBlock("span", _hoisted_12, "-"))
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElTableColumn), {
                      prop: "errorMessage",
                      label: "信息",
                      "min-width": "150"
                    }, {
                      default: _withCtx((scope) => [
                        (scope.row.errorMessage)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              class: _normalizeClass(getMessageClass(scope.row.requestStatus))
                            }, _toDisplayString(scope.row.errorMessage || "-"), 3))
                          : (_openBlock(), _createElementBlock("span", _hoisted_13, "-"))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["data"])), [
                  [_directive_loading, loading.value]
                ])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("span", null, "总记录数：" + _toDisplayString(transactionRecords.value.length), 1),
                _createElementVNode("span", null, "成功：" + _toDisplayString(successCount.value), 1),
                _createElementVNode("span", null, "失败：" + _toDisplayString(failedCount.value), 1),
                _createElementVNode("span", null, "进行中：" + _toDisplayString(pendingCount.value), 1)
              ])
            ])
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]))
    : _createCommentVNode("", true)
}
}

})